/*
 * assets/omnibus-price.js   (Eminent price display, 25 Oct 2026)
 * Small, dependency free, loaded with defer from layout/theme.liquid. Two jobs:
 *
 * 1. Info icon and tooltip on the product page (markup: snippets/omnibus-tooltip.liquid)
 *      mouse        : hover shows it (CSS, only for a fine pointer, so a tap on a phone cannot leave a sticky hover)
 *      keyboard     : focus shows it (CSS :focus-visible), Enter / Space toggle it, Escape hides it (WCAG 1.4.13)
 *      touch        : tap on the icon toggles, a tap anywhere else closes it
 *      placement    : centred on the icon, clamped into the info column and the viewport, arrow moved to the icon
 *    aria-expanded follows the open state. The tooltip text is always in the page and linked with aria-describedby.
 *
 * 2. Swatch app on cards (Grouptify "combined listings", theme setting "change content on card"):
 *    when a visitor clicks a colour swatch on a card, the app swaps title, link, image and price of that card to the other
 *    product, from its own stored prices. That would leave the red tag, the label and the gold badge of the OLD product on the
 *    card and could print a struck price that is not the locked 30-day price. The app calls window.mpg.updatePriceCustom(card, product)
 *    when it exists instead of rewriting the price itself. We use that hook: the old statements are hidden at once, the new
 *    ones are fetched from the theme (Section Rendering API, sections/omnibus-card.liquid, on the URL of the new product, so
 *    market, language and currency are right) and put in place. If the request fails, the statements stay hidden (price only).
 */
(function () {
  'use strict';
  var doc = document;

  /* ------------------------------ 1. tooltip ------------------------------ */
  function place(wrap) {
    var tip = wrap.querySelector('.omnibus-tip');
    var btn = wrap.querySelector('.omnibus-info__btn');
    if (!tip || !btn) return;
    var vw = doc.documentElement.clientWidth, margin = 12;
    var bound = (wrap.closest('.product-single__meta') || doc.body).getBoundingClientRect();
    var lo = Math.max(margin, bound.left), hi = Math.min(vw - margin, bound.right);
    wrap.setAttribute('data-measuring', '');           /* the CSS shows the box invisibly while it is measured (it is display:none otherwise) */
    tip.style.left = '0px';
    var br = btn.getBoundingClientRect(), wr = wrap.getBoundingClientRect(), tw = tip.offsetWidth, cx = br.left + br.width / 2;
    var left = Math.max(lo, Math.min(cx - tw / 2, hi - tw));
    tip.style.left = (left - wr.left) + 'px';
    tip.style.setProperty('--ax', Math.max(10, Math.min(tw - 10, cx - left)) + 'px');
    wrap.removeAttribute('data-measuring');
  }
  function toggle(wrap, open) {
    var on = open == null ? !wrap.classList.contains('is-open') : open;
    wrap.classList.toggle('is-open', on);
    var btn = wrap.querySelector('.omnibus-info__btn');
    if (btn) btn.setAttribute('aria-expanded', String(on));
    if (on) { wrap.removeAttribute('data-dismissed'); place(wrap); }
  }
  function closeAll(except) {
    var open = doc.querySelectorAll('.omnibus-info.is-open');
    for (var i = 0; i < open.length; i++) if (open[i] !== except) toggle(open[i], false);
  }
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.omnibus-info__btn');
    var wrap = btn ? btn.closest('.omnibus-info') : null;
    closeAll(wrap);
    if (wrap) { e.preventDefault(); toggle(wrap); }
  });
  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var all = doc.querySelectorAll('.omnibus-info');
    for (var i = 0; i < all.length; i++) { toggle(all[i], false); all[i].setAttribute('data-dismissed', ''); }
  });
  /* a tooltip dismissed with Escape comes back on the next hover or focus */
  function undismiss(e) {
    var wrap = e.target.closest && e.target.closest('.omnibus-info');
    if (wrap) wrap.removeAttribute('data-dismissed');
  }
  doc.addEventListener('mouseout', undismiss);
  doc.addEventListener('focusout', undismiss);
  function onEnter(e) {
    var wrap = e.target.closest && e.target.closest('.omnibus-info');
    if (wrap) place(wrap);
  }
  doc.addEventListener('mouseover', onEnter);
  doc.addEventListener('focusin', onEnter);
  window.addEventListener('resize', function () {
    var open = doc.querySelectorAll('.omnibus-info.is-open');
    for (var i = 0; i < open.length; i++) place(open[i]);
  });

  /* ------------------------------ 2. swatch app ------------------------------ */
  var SECTION = 'omnibus-card';
  function fragment(html) {
    var t = doc.createElement('template');
    t.innerHTML = html;
    return t.content;
  }
  function hideStatements(card) { card.classList.add('omnibus-pending'); }
  function dropStatements(card) {
    var old = card.querySelectorAll('[data-omnibus="tag"],[data-omnibus="label"],[data-omnibus="badge"]');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
    var strike = card.querySelectorAll('.grid-product__price--original');
    for (var j = 0; j < strike.length; j++) strike[j].parentNode.removeChild(strike[j]);
    card.classList.remove('m1');
  }
  function refreshCard(card) {
    var link = card.querySelector('a.grid-product__link');
    if (!link) return;
    hideStatements(card);
    var url;
    try { url = new URL(link.getAttribute('href'), window.location.href); } catch (err) { dropStatements(card); card.classList.remove('omnibus-pending'); return; }
    url.searchParams.set('sections', SECTION);
    window.fetch(url.pathname + url.search, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (json) {
        var html = json && json[SECTION];
        if (!html) throw new Error('section not rendered');
        var frag = fragment(html);
        var price = frag.querySelector('[data-omnibus="price"]');
        var current = card.querySelector('.grid-product__price');
        if (!price || !current) throw new Error('price block missing');
        dropStatements(card);
        current.parentNode.replaceChild(price, current);
        var tag = frag.querySelector('[data-omnibus="tag"]');
        var content = card.querySelector('.grid-product__content');
        if (tag && content) content.insertBefore(tag, content.firstChild);
        var label = frag.querySelector('[data-omnibus="label"]');
        var rwp = card.querySelector('.review-w-price');
        if (label && rwp) {
          if (rwp.parentNode.classList.contains('eminent-shop')) rwp.parentNode.insertBefore(label, rwp.nextSibling);
          else rwp.appendChild(label);
        }
        var badge = frag.querySelector('[data-omnibus="badge"]');
        if (badge) link.appendChild(badge);
        if (label) card.classList.add('m1');
        card.classList.remove('omnibus-pending');
      })
      .catch(function () {
        /* fail safe: price only. The old tag, label, badge and struck price were hidden; now they are removed. */
        dropStatements(card);
        card.classList.remove('omnibus-pending');
      });
  }
  var mpg = (window.mpg = window.mpg || {});
  mpg.updatePriceCustom = function (card /*, product */) { if (card) refreshCard(card); };
})();
